// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {SvgIconEl} from 'common/components/svg-icon';
import {ModalEl} from 'common/components/modal';
import {IntroStep} from 'common/widgets/intro/types';
import {MarkdownEl} from 'common/components/markdown';
import {LanguageSelectorEl} from 'common/widgets/language-selector';

import type {Intro} from './index';

// TODO opslead
export function template(this: Intro) {
  const SkipButton = () => (
    <ButtonEl
      sel='.skip-button'
      attrs={{
        theme: ButtonTheme.Tertiary,
        href: this.helpers.appendMpSource(`intro-skip`),
      }}
    >
      {this.t(`signup.intro.skip`)}
    </ButtonEl>
  );

  const NextButton = () => (
    <ButtonEl sel='.next-button' attrs={{theme: ButtonTheme.Primary}} on={{click: this.helpers.nextStep}}>
      {this.t(`signup.intro.next`, {emoji: `→`})}
    </ButtonEl>
  );

  const StepButton = ({index, step}: {index: number; step: IntroStep}) => (
    <div
      sel='.step-button'
      class={{active: step.active, visited: step.visited, [step.name]: true}}
      on={{click: () => this.helpers.handleStepClick(index)}}
    >
      <div sel='.step-button-content'>
        <SvgIconEl attrs={{icon: step.icon}} />
        <div sel='.step-button-label'>{this.print(step.text)}</div>
      </div>
      <div sel='.step-button-indicator'>
        {step.visited ? (
          <SvgIconEl attrs={{icon: `circle-checkmark`}} />
        ) : (
          <div sel='.step-button-index'>{index + 1}</div>
        )}
      </div>
    </div>
  );

  // Step 1

  const OverviewCopy = () => (
    <div sel='.content'>
      <div sel='.content-container'>
        <h1 sel='.title'>
          {this.attr(`first-name`)
            ? this.t(`signup.intro.hi-first-name`, {firstName: this.attr(`first-name`)})
            : this.t(`signup.intro.hi-no-name`)}
        </h1>
        <h1 sel='.title'>{this.t(`signup.intro.welcome`)}</h1>
        <p sel='.text'>{this.t(`signup.intro.mixpanel-overview`)}</p>
        <p sel='.text'>{this.t(`signup.intro.key-concepts`)}</p>
        <ButtonEl sel='.next-button' attrs={{theme: ButtonTheme.Primary}} on={{click: this.helpers.nextStep}}>
          {this.t(`signup.intro.get-started`)}
        </ButtonEl>
        {this.attr(`is-full-page`) && <SkipButton />}
      </div>
    </div>
  );

  // Step 2

  const UsersCopy = () => (
    <div sel='.content'>
      <div sel='.content-container'>
        <h1 sel='.title'>{this.t(`signup.intro.unique-user`)}</h1>
        <p sel='.text'>{this.t(`signup.intro.cafe-owner`)}</p>
        <p sel='.text'>{this.t(`signup.intro.cafe-customer`)}</p>
        <p sel='.text'>
          <MarkdownEl attrs={{text: this.t(`signup.intro.customer-details`)}} />
        </p>
        <NextButton />
        {this.attr(`is-full-page`) && <SkipButton />}
      </div>
    </div>
  );

  // Step 3

  const EventsCopy = () => (
    <div sel='.content'>
      <div sel='.content-container'>
        <h1 sel='.title'>{this.t(`signup.intro.track-action`)}</h1>
        <p sel='.text'>
          <MarkdownEl attrs={{text: this.t(`signup.intro.purchase-details`)}} />
        </p>
        <p sel='.text'>
          <MarkdownEl attrs={{text: this.t(`signup.intro.user-action`)}} />
          <MarkdownEl attrs={{text: this.t(`signup.intro.action-details`)}} />
        </p>
        <NextButton />
        {this.attr(`is-full-page`) && <SkipButton />}
      </div>
    </div>
  );

  // Step 4

  const ProfilesCopy = () => (
    <div sel='.content'>
      <div sel='.content-container'>
        <h1 sel='.title'>{this.t(`signup.intro.profiles-purpose`)}</h1>
        <p sel='.text'>
          <MarkdownEl attrs={{text: this.t(`signup.intro.mixpanel-consolidates`)}} />
        </p>
        <p sel='.text'>{this.t(`signup.intro.uncover-insights`)}</p>
        <NextButton />
        {this.attr(`is-full-page`) && <SkipButton />}
      </div>
    </div>
  );

  // Final step

  const CompleteCopy = () => (
    <div sel='.content'>
      <div sel='.content-container'>
        <h1 sel='.title'>{this.t(`signup.intro.great-work`, {emoji: ` 🎉`})}</h1>
        <p sel='.text'>{this.t(`signup.intro.ready-to-start`)}</p>
        <p sel='.text.dashboards-notice'>{this.t(`signup.intro.boards-overview`)}</p>
        {this.attr(`is-full-page`) ? (
          <ButtonEl
            sel='.next-button'
            attrs={{theme: ButtonTheme.Primary, href: this.helpers.appendMpSource(`intro-complete`)}}
          >
            {this.getCtaCopy()}
          </ButtonEl>
        ) : (
          <>
            <ButtonEl
              sel='.next-button'
              attrs={{theme: ButtonTheme.Primary}}
              on={{click: this.helpers.onCloseButtonClick}}
            >
              {this.getCtaCopy()}
            </ButtonEl>
            <ButtonEl
              sel='.learn-more-button'
              attrs={{
                href: `https://mixpanel.com/api/app/demo_project`,
                theme: ButtonTheme.Tertiary,
                target: `_blank`,
              }}
            >
              {this.t(`signup.intro.view-demo`)}
            </ButtonEl>
          </>
        )}
      </div>
    </div>
  );

  const {step} = this.state;
  const steps = this.getIntroSteps();
  const currentStep = steps[step];
  // Don't show a button for the last step
  const buttonSteps = steps.slice(0, -1);

  const IntroContent = () => (
    <div sel='.intro-page'>
      <div sel='.intro-container' class={{'is-full-page': this.attr(`is-full-page`)}}>
        <div sel='.header'>
          <LanguageSelectorEl />
        </div>
        <div sel='.steps'>
          <div sel='.steps-container'>
            {buttonSteps.map((buttonStep, idx) => (
              <StepButton index={idx} step={buttonStep} />
            ))}
          </div>
        </div>
        {currentStep.name === this.helpers.IntroStepName.Mixpanel ? (
          <>
            <OverviewCopy />
          </>
        ) : currentStep.name === this.helpers.IntroStepName.Users ? (
          <>
            <UsersCopy />
          </>
        ) : currentStep.name === this.helpers.IntroStepName.Events ? (
          <>
            <EventsCopy />
          </>
        ) : currentStep.name === this.helpers.IntroStepName.Profiles ? (
          <>
            <ProfilesCopy />
          </>
        ) : currentStep.name === this.helpers.IntroStepName.Complete ? (
          <>
            <CompleteCopy />
          </>
        ) : null}
      </div>
    </div>
  );

  return this.attr(`is-full-page`) ? (
    <IntroContent />
  ) : (
    <ModalEl
      attrs={{
        elref: `introModal`,
        closeable: true,
        open: true,
        'grow-to-content': true,
      }}
    >
      <IntroContent />
    </ModalEl>
  );
}
